// Persistent "Need Help?" support button — fixed bottom-right on every authenticated page.
// Opens the Beyond HQ support ticket form in a new tab so the user never loses their work.
// Sits ABOVE the platform's fixed "Powered by Beyond Measure" badge (bottom:14px/right:14px,
// see public/index.html#beyond-measure-badge) so the two never overlap. Collapses into a
// slim tab hugging the right edge (not a full dismiss) so it's one click away again —
// also reachable from the Help icon in the Topbar (see Topbar.jsx). Auto-hides whenever it
// would overlap a modal/dialog or a bottom nav bar (tagged `data-help-avoid`).
import { useEffect, useRef, useState } from "react";
import { LifeBuoy, X, ChevronLeft } from "lucide-react";

export const SUPPORT_URL = "https://hq.beyondmeasure.global/ticket/PR";
const COLLAPSE_KEY = "pr_help_button_collapsed";
const REOPEN_EVENT = "pr:reopen-help-button";

export function openSupport() {
    let url = SUPPORT_URL;
    try {
        url = `${SUPPORT_URL}?from=${encodeURIComponent(window.location.href)}`;
    } catch (_e) {
        // window.location unavailable — fall back to the plain support URL
    }
    window.open(url, "_blank", "noopener,noreferrer");
}

// Called by the Topbar's Help icon so the floating button expands again even if collapsed.
export function reopenHelpButton() {
    try { sessionStorage.removeItem(COLLAPSE_KEY); } catch (_e) { /* storage unavailable */ }
    try { window.dispatchEvent(new Event(REOPEN_EVENT)); } catch (_e) { /* no-op */ }
}

export default function NeedHelpButton() {
    const [collapsed, setCollapsed] = useState(() => {
        try { return sessionStorage.getItem(COLLAPSE_KEY) === "1"; } catch (_e) { return false; }
    });
    const [avoiding, setAvoiding] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        const onReopen = () => setCollapsed(false);
        window.addEventListener(REOPEN_EVENT, onReopen);
        return () => window.removeEventListener(REOPEN_EVENT, onReopen);
    }, []);

    // Collision/visibility watch — any open modal/dialog, or any `data-help-avoid`
    // element (bottom nav bars, sticky save/submit footers) overlapping our own
    // fixed position, fades the button out and makes it non-interactive.
    useEffect(() => {
        let raf = null;
        const check = () => {
            raf = null;
            const wrap = wrapRef.current;
            if (!wrap) return;
            if (document.querySelector('[role="dialog"], .fixed.inset-0')) {
                setAvoiding(true);
                return;
            }
            const rect = wrap.getBoundingClientRect();
            const pad = 10;
            let hit = false;
            document.querySelectorAll("[data-help-avoid]").forEach((el) => {
                if (hit) return;
                const r = el.getBoundingClientRect();
                if (r.width === 0 && r.height === 0) return;
                const overlaps = !(
                    rect.right + pad < r.left ||
                    rect.left - pad > r.right ||
                    rect.bottom + pad < r.top ||
                    rect.top - pad > r.bottom
                );
                if (overlaps) hit = true;
            });
            setAvoiding(hit);
        };
        const schedule = () => { if (raf == null) raf = requestAnimationFrame(check); };
        schedule();
        window.addEventListener("scroll", schedule, true);
        window.addEventListener("resize", schedule);
        const interval = setInterval(schedule, 400);
        return () => {
            window.removeEventListener("scroll", schedule, true);
            window.removeEventListener("resize", schedule);
            clearInterval(interval);
            if (raf != null) cancelAnimationFrame(raf);
        };
    }, [collapsed]);

    const toggleCollapsed = (next) => {
        setCollapsed(next);
        try { sessionStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch (_e) { /* storage unavailable */ }
    };

    // bottom-16 (64px) keeps BOTH states clear of the fixed "Powered by Beyond
    // Measure" badge, which occupies roughly bottom:14-44px / right:14-174px.
    if (collapsed) {
        return (
            <div
                ref={wrapRef}
                data-testid="need-help-button-wrap"
                className={`fixed z-40 bottom-16 right-0 transition-all duration-300 ${
                    avoiding ? "opacity-0 translate-x-4 pointer-events-none" : "opacity-100"
                }`}
            >
                <button
                    data-testid="need-help-expand-button"
                    onClick={() => toggleCollapsed(false)}
                    aria-label="Show Need Help button"
                    title="Need Help?"
                    className="flex items-center justify-center w-6 h-11 rounded-l-full text-white
                               shadow-[0_4px_14px_rgba(0,0,0,0.18)] hover:w-7 transition-all duration-200"
                    style={{ background: "#9F4212" }}
                >
                    <ChevronLeft size={13} />
                </button>
            </div>
        );
    }

    return (
        <div
            ref={wrapRef}
            data-testid="need-help-button-wrap"
            className={`fixed z-40 bottom-16 right-4 transition-all duration-200 ${
                avoiding ? "opacity-0 scale-75 translate-y-2 pointer-events-none" : "opacity-100 scale-100"
            }`}
        >
            <button
                data-testid="need-help-button"
                onClick={openSupport}
                aria-label="Need Help? Opens support in a new tab"
                title="Need Help?"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full text-white
                           shadow-[0_6px_16px_rgba(0,0,0,0.18)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.24)]
                           transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                style={{ background: "#9F4212" }}
            >
                <LifeBuoy size={17} />
            </button>
            <button
                data-testid="need-help-minimize-button"
                onClick={(e) => { e.stopPropagation(); toggleCollapsed(true); }}
                aria-label="Minimise help button"
                title="Minimise"
                className="absolute -top-1 -right-1 w-[17px] h-[17px] rounded-full bg-white border
                           text-[#6F6A64] grid place-items-center shadow-sm hover:text-[#9F4212] hover:border-[#9F4212] transition-colors"
                style={{ borderColor: "#E5E0DA" }}
            >
                <X size={10} strokeWidth={2.75} />
            </button>
        </div>
    );
}
